/**
 * Formats de date, de nombre et de libellé de Mon compte v2 (maquette /msc, script PUNCH_UI :
 * time, dateShort, dateExpiry, dayLong, monthYear, plural). Fonctions pures.
 *
 * Les dates Mindbody arrivent sans fuseau (« 2026-10-05T07:45:00 ») : new Date() les lit en heure
 * locale, comme la v1 (parseISO / new Date dans screens/Account).
 */

const fmt = (d: Date, o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('fr-FR', o).format(d);

/** Date Mindbody → Date ; null si absente ou illisible. */
export function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** « 07:45 » */
export const fmtTime = (d: Date) => fmt(d, { hour: '2-digit', minute: '2-digit', hour12: false });

/** « 5 oct. » */
export const fmtDateShort = (d: Date) => fmt(d, { day: 'numeric', month: 'short' });

/** « lundi » */
export const fmtDayLong = (d: Date) => fmt(d, { weekday: 'long' });

/** « 05/10/2026 » (tableaux de la maquette, comme la v1). */
export const fmtDateNum = (d: Date) => fmt(d, { day: '2-digit', month: '2-digit', year: 'numeric' });

/** « mars 2024 » */
export const fmtMonthYear = (d: Date) => fmt(d, { month: 'long', year: 'numeric' });

/**
 * Date d'expiration : l'année seulement si elle n'est pas l'année en cours (règle de la maquette,
 * SPEC MC3) — « 29 janv. 2027 », « 15 oct. ».
 */
export function fmtDateExpiry(d: Date, now: Date = new Date()): string {
  return d.getFullYear() === now.getFullYear()
    ? fmt(d, { day: 'numeric', month: 'short' })
    : fmt(d, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Jours entiers jusqu'à une date (arrondi au supérieur, comme la maquette). */
export function daysUntil(d: Date, now: Date = new Date()): number {
  return Math.ceil((d.getTime() - now.getTime()) / 86400000);
}

/** « 1 crédit », « 3 crédits » ; `many` pour les pluriels irréguliers. */
export function plural(n: number, one: string, many?: string): string {
  return `${n} ${Math.abs(n) < 2 ? one : many ?? `${one}s`}`;
}

/** Initiales de la carte d'identité : « MD ». */
export function initials(firstName?: string | null, lastName?: string | null): string {
  const first = (firstName ?? '').trim().charAt(0);
  const last = (lastName ?? '').trim().charAt(0);
  return `${first}${last}`.toUpperCase();
}

/** Montant en euros : « 139 € », « 139,50 € » ; « — » si absent. */
export function fmtEuros(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** « 2026-03 » → « mars » ; mois de 1 à 12 → nom français. */
export function monthName(month: number): string {
  return fmt(new Date(2026, month - 1, 1), { month: 'long' });
}

/** Clé de mois « AAAA-MM » d'une date locale. */
export function monthKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
